'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';

import { useCurrency } from '@/hooks/useCurrency';

import { YearPoint } from '../engine';

import { roundHundred } from './chartFormat';

type Props = {
  /** null when the running totals never meet inside the horizon */
  crossoverYear: number | null;
  years: YearPoint[];
};

const UTILITY_COLOR = 'uiChartGas';
const SOLAR_COLOR = 'uiChartElectric';

type Geometry = {
  captionH: number;
  fontPx: number;
  plotH: number;
  topPad: number;
  width: number;
};

// Fixed widths, never 100%: 300 clears the 327px mobile content box and 620 the
// 720px figure, so neither breakpoint can push the page into horizontal scroll.
const MOBILE: Geometry = { captionH: 14, fontPx: 9, plotH: 120, topPad: 16, width: 300 };
const DESKTOP: Geometry = { captionH: 18, fontPx: 12, plotH: 180, topPad: 22, width: 620 };

type CurveProps = {
  crossoverYear: number | null;
  geometry: Geometry;
  years: YearPoint[];
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

// SVG has no render-time text metrics, and the crossover caption is the only
// variable-width label competing for the axis row. 0.55em per character is a
// safe upper bound at these tick sizes.
const textWidthPx = (text: string, fontPx: number): number => text.length * fontPx * 0.55;

// Two running totals from the same walk: what the utility collects, and what
// the utility plus a paid-for system collects. Where they meet is the payback.
const Curve = ({ crossoverYear, geometry, years }: CurveProps) => {
  const { formatCurrency } = useCurrency();
  const colors = DS.useChartColors();
  const { captionH, fontPx, plotH, topPad, width } = geometry;

  const height = topPad + plotH + captionH;
  const baselineY = topPad + plotH;
  const final = years[years.length - 1];
  const maxUsd = Math.max(final.doNothingUsd, final.withSolarUsd);

  const xFor = (year: number) => 4 + (year / final.year) * (width - 10);
  const yFor = (usd: number) => baselineY - (usd / maxUsd) * plotH;
  const line = (pick: (point: YearPoint) => number) =>
    years.map((point) => `${xFor(point.year).toFixed(1)},${yFor(pick(point)).toFixed(1)}`).join(' ');

  const crossing = crossoverYear === null ? undefined : years[crossoverYear];
  const crossingText = crossing ? `${crossing.calendarYear} — paid for itself` : '';
  const crossingHalfPx = textWidthPx(crossingText, fontPx) / 2;
  // Centred on the crossing, then pulled inside the viewBox: a payback landing
  // near the horizon would otherwise render off the right edge
  const crossingX = clamp(crossing ? xFor(crossing.year) : 0, crossingHalfPx + 2, width - crossingHalfPx - 2);

  // The caption names its own year, so it takes the axis row from either end
  // tick it would collide with rather than overprinting it. Start ticks anchor
  // left of their x, end ticks right of it.
  const tickGutterPx = 4;
  const startTickEnd = xFor(1) + textWidthPx(String(years[1].calendarYear), fontPx) + tickGutterPx;
  const finalTickStart = xFor(final.year) - textWidthPx(String(final.calendarYear), fontPx) - tickGutterPx;
  const showStartTick = !crossing || startTickEnd < crossingX - crossingHalfPx;
  const showFinalTick = !crossing || finalTickStart > crossingX + crossingHalfPx;

  return (
    <x.svg
      aria-label={`Running total over 20 years, state averages: the utility only against the utility plus a solar system, ${
        crossing ? `crossing in ${crossing.calendarYear}` : 'never crossing inside the horizon'
      }`}
      flex="none"
      height={height}
      role="img"
      width={width}
    >
      <x.polyline
        fill="none"
        points={line(({ withSolarUsd }) => withSolarUsd)}
        stroke={SOLAR_COLOR}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
      <x.polyline
        fill="none"
        points={line(({ doNothingUsd }) => doNothingUsd)}
        stroke={UTILITY_COLOR}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />

      {/* The utility line carries the endpoint figure; the solar line is read off the strip below */}
      <x.text
        fill={colors.axisLabel}
        fontSize={`${fontPx}px`}
        textAnchor="end"
        x={xFor(final.year)}
        y={yFor(final.doNothingUsd) - 8}
      >
        {formatCurrency(roundHundred(final.doNothingUsd))}
      </x.text>

      {crossing && (
        <>
          <x.circle
            cx={xFor(crossing.year)}
            cy={yFor(crossing.doNothingUsd)}
            fill="uiBg"
            r={4}
            stroke="uiHeadingCopy"
            strokeWidth={2}
          />
          <x.text fill={colors.tickLabel} fontSize={`${fontPx}px`} textAnchor="middle" x={crossingX} y={height - 2}>
            {crossingText}
          </x.text>
        </>
      )}

      {showStartTick && (
        <x.text fill={colors.tickLabel} fontSize={`${fontPx}px`} textAnchor="start" x={xFor(1)} y={height - 2}>
          {years[1].calendarYear}
        </x.text>
      )}
      {showFinalTick && (
        <x.text fill={colors.tickLabel} fontSize={`${fontPx}px`} textAnchor="end" x={xFor(final.year)} y={height - 2}>
          {final.calendarYear}
        </x.text>
      )}
    </x.svg>
  );
};

// One chart per breakpoint: SVG text can't reflow responsively
export const CumulativeCostChart = ({ crossoverYear, years }: Props) => (
  <>
    <x.div display={{ _: 'block', md: 'none' }}>
      <Curve crossoverYear={crossoverYear} geometry={MOBILE} years={years} />
    </x.div>
    <x.div display={{ _: 'none', md: 'block' }}>
      <Curve crossoverYear={crossoverYear} geometry={DESKTOP} years={years} />
    </x.div>
  </>
);
